import BaseExtension from './base';

import {
   createElement,
   addClass,
   isEmpty,
   each,
   toArray,
   removeClass,
   last
} from '../utils';

import opener from './helpers/clickOpener';
import openerHeightChecker from './helpers/openerHeightChecker';
import popover from './helpers/popover';
import { secondsToTime } from './helpers/timeCodeToSeconds';
import {
   UNAPPROVED_TIMEDATA_STATUS,
   TRANSLATABLE_TIMEDATA_STATUS
} from '../config/statuses';

import createSvg, { DOWNLOAD, BACK, NEXT } from '../svgs';
import loadVTT from './helpers/vtt-loader';
import load from './helpers/transport';
import { saveAs } from './helpers/FileSaver';

const CN = 'rmp-ext-download';
const BUTTON_CN = `${CN}__button`;
const SELECTOR_CN = `${CN}__selector`;
const SELECTOR_OPEN_CN = `${SELECTOR_CN}--open`;
const SELECTOR_SHOW_ADDITIONAL_CN = `${SELECTOR_CN}--additional`;
const MORE_CN = `${CN}__more`;

const MAIN_STEP_CN = `${CN}__main-step`;
const ADDITIONAL_STEP_CN = `${CN}__additional-step`;

const BACK_BTN_CN = `${CN}__back`;

const DOWNLOADCC_CN = `${CN}__download-cc`;

export default class DownloadExtension extends BaseExtension {
   constructor(player, state, options) {
      super(player, state, options);
      this.logger.debug('download:extension:creating');

      this.render();
      this.init();

      this.logger.debug('download:extension:created');
   }

   render() {
      /** @type {HTMLMediaElement} */
      this.$el = createElement('span', { class: CN, 'data-testid': 'download' });

      this.$button = createElement(
         'button',
         { type: 'button', class: BUTTON_CN },
         this.$el
      );

      this.$selector = createElement('div', { class: SELECTOR_CN }, this.$el);
      this.$link = createElement(
         'a',
         { style: 'display: none;' },
         this.$selector
      );

      this.$mainStep = createElement(
         'div',
         { class: MAIN_STEP_CN },
         this.$selector
      );

      if (this.options.showDownloadVideo) {
         this.$video = createElement(
            'button',
            { type: 'button', class: DOWNLOADCC_CN },
            this.$mainStep
         );

         const text = createElement('span', {}, this.$video);
         text.textContent = this.$t('video-drm');
         addClass(this.$video, MORE_CN);

         _createMore(this.$video);
      }

      if (this.options.showDownloadCC) {
         this.$cc = createElement(
            'button',
            { type: 'button', class: DOWNLOADCC_CN },
            this.$mainStep
         );

         const ccText = createElement('span', {}, this.$cc);
         ccText.textContent = this.$t('cc');
         addClass(this.$cc, MORE_CN);

         _createMore(this.$cc);

         this.$transcript = createElement(
            'button',
            { type: 'button', class: DOWNLOADCC_CN },
            this.$mainStep
         );

         const transcriptText = createElement('span', {}, this.$transcript);
         transcriptText.textContent = this.$t('transcript');
         addClass(this.$transcript, MORE_CN);

         _createMore(this.$transcript);
      }

      if (this.options.downloadVideoURL) {
         this.$mp4 = createElement(
            'button',
            { type: 'button', class: DOWNLOADCC_CN },
            this.$mainStep
         );
         const mp4 = createElement('span', {}, this.$mp4);
         mp4.textContent = this.$t('video-drm');
      }

      this.$svg = createSvg(this.$button, DOWNLOAD);
      this.$spinner = createElement('img', {
         src: '/images/Spinner-small.gif',
         height: '26',
         width: '26'
      });

      this.$additionalStep = createElement(
         'div',
         { class: ADDITIONAL_STEP_CN },
         this.$selector
      );

      this.$back = createElement(
         'button',
         { type: 'button', class: BACK_BTN_CN },
         this.$additionalStep
      );
      const $backSpan = createElement('span', { class: 'arrow' }, this.$back);
      createSvg($backSpan, BACK);

      this.$backText = createElement('span', {}, this.$back);

      this.$additional = createElement('div', { class: 'tab-content' }, this.$additionalStep);
   }

   init() {
      const $button = this.$button,
         $selector = this.$selector,
         reposition = false,
         closeOnClick = false;
      const options = this.options;
      opener(this, {
         $button,
         $selector,
         SELECTOR_OPEN_CN,
         reposition,
         closeOnClick,
         firstFocusItemSelector: `.${DOWNLOADCC_CN}`,
         toMainFn: this._toMain.bind(this)
      });
      popover(this, { $button, text: this.$t('download') });

      if (isEmpty(options.downloadCCLanguages) && !options.showDownloadCC && !options.downloadVideoURL) {
         this.addEvent(this.$el, 'click', this.downloadDRMVideo.bind(this, null));
         return;
      }

      if (options.downloadVideoURL) {
         this.addEvent(this.$mp4, 'click', this.performClick.bind(this, options.downloadVideoURL));
      }

      this.addEvent(this.$back, 'click', () => this._toMain());

      if (options.showDownloadVideo) {
         this.addEvent(this.$video, 'click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this._toAdditional(this.$t('video-drm'));
            this._renderVideos();
         });
      }

      if (options.showDownloadCC) {
         this.addEvent(this.$transcript, 'click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this._toAdditional(this.$t('transcript'));
            this._renderTranscripts();
         });

         this.addEvent(this.$cc, 'click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this._toAdditional(this.$t('cc'));
            this._renderClosedCaptions();
         });
      }
   }

   downloadDRMVideo(ccLanguage) {
      let url = this.options.downloadURL;
      if (ccLanguage !== null) {
         url += '?ccLang=' + ccLanguage;
      }

      this.bus.emit('downloadRequested', url, this.performClick.bind(this));
      this.$button.removeChild(this.$svg);
      this.$button.appendChild(this.$spinner);
   }

   downloadTranscript(cc) {
      const filename = `transcript__${cc.srclang}.txt`;
      if (cc.type === 'json') {
         load({
            url: cc.src,
            success: (resp) =>
               this.prepareTranscriptForDownload(JSON.parse(resp.data), filename)
         });
      } else {
         loadVTT(cc.src, (m) => this.prepareTranscriptForDownload(m, filename));
      }
   }

   prepareTranscriptForDownload(segments, filename) {
      const { text } = segments.reduce(
         ({ prevSegmentTime, text }, segment) => {
            if (prevSegmentTime > 0 && segment.from >= prevSegmentTime + 0.1) {
               text += '\r\n';
            }
            const segmentText = segment.text
               .replace(/(\r?\n|\r)$/, '')
               .replace(/<br\s*\/?>/gm, '\r\n');
            text = text + segmentText + '\r\n';
            return { prevSegmentTime: segment.to, text };
         },
         { text: '', prevSegmentTime: 0 }
      );

      var blob = new Blob([text], {
         type: 'text/plain;charset=utf-8'
      });

      saveAs(blob, filename);
   }

   downloadCC(cc) {
      const filename = `captions__${cc.srclang}.vtt`;
      if (cc.type === 'json') {
         load({
            url: cc.src,
            success: (resp) =>
               this.prepareCCForDownload(JSON.parse(resp.data), filename)
         });
      } else {
         load({
            url: cc.src,
            success: (resp) => {
               var blob = new Blob([resp.data], {
                  type: 'text/plain;charset=utf-8'
               });

               saveAs(blob, filename);
            }
         });
      }
   }

   prepareCCForDownload(segments, filename) {
      const text = segments.reduce((memo, segment) => {
         if (!memo) {
            memo += 'WEBVTT\r\n';
         }
         memo += '\r\n';
         memo +=
        secondsToTime(segment.from, false, true) +
        ' --> ' +
        secondsToTime(segment.to, false, true);
         memo += '\r\n';
         memo += segment.text;
         memo += '\r\n';
         return memo;
      }, '');

      var blob = new Blob([text], {
         type: 'text/plain;charset=utf-8'
      });

      saveAs(blob, filename);
   }

   getElement() {
      return this.$el;
   }

   destroy() {
      super.destroy();

      const parent = this.$el.parentNode;
      parent.removeChild(this.$el);
      this.$el = null;
   }

   performClick(url) {
      var link = document.createElement('a', {href: url, target: '_blank'});
      link.href = url;
      link.target = '_blank';
      link.className = 'rmp-ext-download-hidden';
      link.download = last(url.split('/')) || 'video.mp4';
      document.body.appendChild(link);
      link.click();
      // document.body.removeChild(link);

      try {
         this.$button.removeChild(this.$spinner);
         this.$button.appendChild(this.$svg);
      } catch (e) {
      // noop
      }
   }

   _toMain() {
      removeClass(this.$selector, SELECTOR_SHOW_ADDITIONAL_CN);
      openerHeightChecker(this.player, this.$mainStep);

      // Accessability: Focus on first item of main page
      const mainStepItems = document.querySelectorAll(`.${MAIN_STEP_CN} > .${DOWNLOADCC_CN}`);
      if (mainStepItems[0]) mainStepItems[0].focus();
   }

   _toAdditional(text) {
      const items = toArray(this.$additional.childNodes);
      each(items, (btn) => this.$additional.removeChild(btn));
      addClass(this.$selector, SELECTOR_SHOW_ADDITIONAL_CN);
      this.$backText.textContent = text;

      openerHeightChecker(this.player, this.$additionalStep);

      // Accessability: focus on the first item
      this.$back.focus();
   }

   _renderVideos() {
      const btn = createElement(
         'button',
         { type: 'button', class: DOWNLOADCC_CN },
         this.$additional
      );

      this.addEvent(btn, 'click', () => this.downloadDRMVideo(null));
      const text = createElement('span', {}, btn);
      text.textContent = this.$t('video-only');

      each(this.options.downloadCCLanguages, (c) => {
         const btn = createElement(
            'button',
            { type: 'button', class: DOWNLOADCC_CN },
            this.$additional
         );

         this.addEvent(btn, 'click', () => this.downloadDRMVideo(c.code));
         const text = createElement('span', {}, btn);
         text.textContent = `${this.$t('video-and')} ${c.label} CC`;
      });
   }

   _renderTranscripts() {
      each(this.options.cc.filter(isApprovedCC), (cc) => {
         const btn = createElement(
            'button',
            { type: 'button', class: DOWNLOADCC_CN },
            this.$additional
         );

         this.addEvent(btn, 'click', () => this.downloadTranscript(cc));
         const text = createElement('span', {}, btn);
         text.textContent = cc.label;
      });
   }

   _renderClosedCaptions() {
      each(this.options.cc.filter(isApprovedCC), (cc) => {
         const btn = createElement(
            'button',
            { type: 'button', class: DOWNLOADCC_CN },
            this.$additional
         );

         this.addEvent(btn, 'click', () => this.downloadCC(cc));
         const text = createElement('span', {}, btn);
         text.textContent = cc.label;
      });
   }
}

function _createMore($parent) {
   const span = createElement('span', { class: 'arrow' }, $parent);
   createSvg(span, NEXT);
   return span;
}

function isApprovedCC(cc) {
   return (
      cc.state !== UNAPPROVED_TIMEDATA_STATUS &&
    cc.state !== TRANSLATABLE_TIMEDATA_STATUS
   );
}
